import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import SelectListItem, {
  ListItem,
} from "@coworker/apprestructured/src/task-management/components/SelectListItem/SelectListItem";

export type SelectListItemCreatorFragmentConfigProps = {
  title: string;
  field: string;
  items: ListItem[];
};

type SelectListItemCreatorFragmentProps = {
  configProps: SelectListItemCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function SelectListItemCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectListItemCreatorFragmentProps) {
  const { field, title, items } = configProps;
  const { task, onTaskChange, taskType } = commonProps;

  const listItem = validateInitialDetailsFieldValue<ListItem>(
    getByPath(task, field),
    field,
    taskType
  );

  const getItemByValue = useCallback(
    (value: string) => items.find((item) => item.value === value) ?? null,
    [items]
  );

  const resolveSelectedItem = useCallback(() => {
    if (isLoading(listItem)) {
      return null;
    }

    if (listItem?.value) {
      return getItemByValue(listItem.value);
    }

    return null;
  }, [getItemByValue, items, listItem]);

  const selectedItem = resolveSelectedItem();

  const handleConfirmClick = useCallback(
    (newItem: ListItem | null) => {
      onTaskChange(
        setByPath(field, {
          value: newItem?.value ?? "",
          label: newItem?.label ?? "",
        })
      );
    },
    [field, onTaskChange]
  );

  const valid = !isLoading(listItem) && listItem !== null;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (isLoading(listItem)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <SelectListItem
      title={title}
      selectedItem={selectedItem}
      items={items}
      onConfirmClick={handleConfirmClick}
    />
  );
}

export default SelectListItemCreatorFragment;
